import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { firebaseAuth } from "@/shared/lib/firebase";
import { useAuth } from "@/shared/auth/useAuth";
import { isAdminRole } from "@/shared/types/role";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Logo } from "@/shared/components/brand/Logo";
import { crashReporting } from "@/shared/lib/crash-reporting";
import { sanitizeRedirectPath } from "./safe-redirect";

// Firebase auth error codes that represent normal user-correctable states
// (wrong password, unknown email, etc.). We surface them to the UI via
// localized copy but do NOT funnel them into crashReporting — they're the
// auth equivalent of a 4xx and would just create noise. Anything outside
// this set — including network failures — gets reported so operators have
// the same signal for auth as they do for the API (http-client reports
// network errors too).
const EXPECTED_AUTH_ERROR_CODES: ReadonlySet<string> = new Set([
  "auth/invalid-email",
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
  "auth/user-not-found",
  "auth/wrong-password",
  "auth/missing-password",
  "auth/user-disabled",
  "auth/too-many-requests",
]);

interface StoredLocation {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
}

interface LocationState {
  from?: StoredLocation;
}

function composeReturnTarget(from: StoredLocation | undefined): string | undefined {
  if (!from) return undefined;
  const parts: string[] = [];
  if (typeof from.pathname === "string") parts.push(from.pathname);
  if (typeof from.search === "string") parts.push(from.search);
  if (typeof from.hash === "string") parts.push(from.hash);
  const composed = parts.join("");
  return composed.length > 0 ? composed : undefined;
}

export function LoginPage() {
  const { t } = useTranslation();
  const { status, user, rejection } = useAuth();
  const location = useLocation();
  const rawFrom = composeReturnTarget((location.state as LocationState | null)?.from);
  const from = sanitizeRedirectPath(rawFrom);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When backend verification rejects the session, the reason surfaces on the
  // auth context. Drop out of the verifying state so the form + the
  // explanatory banner render again. This is state sync to the external auth
  // context, not a data fetch, so the "no useEffect(fetch)" rule doesn't apply.
  useEffect(() => {
    if (rejection !== null) setVerifying(false);
  }, [rejection]);

  if (status === "authenticated" && user) {
    if (!isAdminRole(user.role)) {
      return <Navigate to="/403" replace />;
    }
    return <Navigate to={from} replace />;
  }

  // A Firebase sign-in succeeded but the backend account is still being
  // verified (`verifying`), or the app is restoring an existing session on
  // cold start (`status === "loading"`). Lock the screen so the operator can't
  // double-submit and gets feedback instead of the bare form. Without this,
  // there's a window where valid credentials show the login form again —
  // confusing, and an invitation to re-submit.
  if (status === "loading" || verifying) {
    return (
      <div
        data-testid="login-verifying"
        className="flex min-h-screen items-center justify-center text-muted-foreground"
      >
        {t("auth.verifyingAccount")}
      </div>
    );
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      // Firebase accepted the credentials. AuthProvider's listener now verifies
      // the backend account; keep the screen locked until that resolves into
      // either an authenticated redirect or a rejection (cleared by the effect).
      setVerifying(true);
    } catch (err) {
      const code =
        err != null && typeof err === "object" && "code" in err
          ? String((err as { code: unknown }).code)
          : "";
      const normalized = err instanceof Error ? err : new Error(String(err));
      if (!EXPECTED_AUTH_ERROR_CODES.has(code)) {
        // Unexpected auth failure — route through the same observability
        // contract as 5xx/network errors in http-client so operators have
        // one place to look.
        crashReporting.recordError(normalized, {
          endpoint: "firebase/signInWithEmailAndPassword",
          code: code || "unknown",
        });
      }
      if (code === "auth/user-disabled") {
        setError(t("auth.signInErrorDisabled"));
      } else if (code === "auth/too-many-requests") {
        setError(t("auth.signInErrorTooManyRequests"));
      } else if (code === "auth/network-request-failed") {
        setError(t("auth.signInErrorNetwork"));
      } else {
        setError(t("auth.signInError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-screen" className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/*
        Brand panel — dark/black background with dark-variant logo lockup.
        This is a decorative hero shown only on desktop (lg+). Screen
        readers should land directly on the sign-in form on the right, so
        the whole panel is hidden from the accessibility tree. The form
        still exposes the product name via its <h1> and subtitle.
      */}
      <aside
        aria-hidden
        className="relative hidden flex-col justify-between overflow-hidden bg-wander-black p-12 text-white lg:flex"
      >
        <Logo variant="dark" size="lg" />
        <div className="relative z-10 max-w-md space-y-4">
          <p className="brand-eyebrow !text-wander-orange">{t("auth.brandEyebrow")}</p>
          <h2 className="text-3xl text-white">{t("auth.brandHeadline")}</h2>
          <p className="font-sans text-sm font-normal leading-relaxed text-white/70">
            {t("auth.brandTagline")}
          </p>
        </div>
        {/* Orange glow accent — brand color as atmosphere, not body copy */}
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-wander-orange/30 blur-3xl"
        />
      </aside>

      {/* Form panel — white with compact card */}
      <div className="flex items-center justify-center bg-background p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center lg:hidden">
            <Logo variant="light" size="md" />
          </div>
          <header className="space-y-2">
            <p className="brand-eyebrow">{t("auth.signInEyebrow")}</p>
            <h1 className="text-3xl">{t("auth.signInTitle")}</h1>
            <p className="font-sans text-sm font-normal text-muted-foreground">
              {t("auth.signInSubtitle")}
            </p>
          </header>
          {/*
            Backend verification rejected the Firebase session. This is distinct
            from a sign-in credential error (handled inline in the form): the
            credentials were valid but the account isn't usable in the backoffice.
          */}
          {rejection === "not-provisioned" && (
            <div
              data-testid="login-not-provisioned"
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {t("auth.accountNotProvisioned")}
            </div>
          )}
          {rejection === "verification-failed" && (
            <div
              data-testid="login-verification-failed"
              role="alert"
              className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            >
              <p>{t("auth.verificationFailed")}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="verification-retry-button"
                onClick={() => window.location.reload()}
              >
                {t("auth.verificationRetry")}
              </Button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="h-11 w-full text-base font-bold uppercase tracking-wider"
              disabled={submitting}
              data-testid="sign-in-button"
            >
              {submitting ? t("auth.signingIn") : t("auth.signInButton")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
